'use client';

import React, { useState, useEffect } from 'react';
import { 
  DocumentArrowUpIcon, 
  XMarkIcon, 
  PlusIcon,
  LinkIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { professionalInfoApi } from '@/app/services/api';
import { UserProfessionalInfo, Project, JobExperience, ResearchExperience, OtherLink } from '@/app/types/interfaces';
import { useAuth } from '@/app/Components/AuthProvider';

export default function ProfessionalInfoSection({ userId }: { userId: string | undefined }) {
  const { user } = useAuth();
  const [info, setInfo] = useState<UserProfessionalInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [extractingResume, setExtractingResume] = useState(false);
  const [extractingTranscript, setExtractingTranscript] = useState(false);

  useEffect(() => {
    if (userId) loadInfo();
  }, [userId]);

  const loadInfo = async () => {
    if (!userId) return;
    try {
      const data = await professionalInfoApi.get(userId);
      setInfo(data || {
        id: 0,
        user_id: userId,
        resume_text: null,
        transcript_text: null,
        personal_statement: null,
        projects: [],
        job_experiences: [],
        research: [],
        other_links: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error loading professional info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file: File, type: 'resume' | 'transcript') => {
    if (!userId) return;
    
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }
    
    if (type === 'resume') setExtractingResume(true);
    else setExtractingTranscript(true);

    try {
      const extractedText = await professionalInfoApi.extractAndStoreDocument(userId, file, type);
      setInfo(prev => prev ? {
        ...prev,
        [`${type}_text`]: extractedText,
      } : null);
      alert(`${type === 'resume' ? 'Resume' : 'Transcript'} text extracted successfully!`);
    } catch (error) {
      console.error(`Error extracting ${type}:`, error);
      alert(`Failed to extract text from ${type}. Please try again.`);
    } finally {
      if (type === 'resume') setExtractingResume(false);
      else setExtractingTranscript(false);
    }
  };

  const handleDeleteDocument = async (type: 'resume' | 'transcript') => {
    if (!userId || !confirm(`Are you sure you want to clear the extracted ${type} text?`)) return;
    
    try {
      await professionalInfoApi.clearDocument(userId, type);
      setInfo(prev => prev ? {
        ...prev,
        [`${type}_text`]: null,
      } : null);
    } catch (error) {
      console.error(`Error clearing ${type}:`, error);
    }
  };

  const handleSave = async () => {
    if (!userId || !info) return;
    setSaving(true);
    try {
      await professionalInfoApi.upsert(userId, info);
      alert('Professional information saved successfully!');
    } catch (error) {
      console.error('Error saving professional info:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (!info) return null;

  return (
    <div className="space-y-6">
      {/* Document Uploads */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Documents
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          Upload your resume and transcript as PDF files. The text will be extracted and stored for AI email generation.
        </p>
        
        <div className="space-y-4">
          {/* Resume Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Resume
            </label>
            {info.resume_text ? (
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-control">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Resume text extracted ({info.resume_text.length} characters)
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument('resume')}
                    className="text-red-600 hover:text-red-700"
                    title="Clear resume text"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    View extracted text
                  </summary>
                  <pre className="mt-2 p-2 bg-white dark:bg-slate-800 rounded text-xs overflow-auto max-h-40 text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                    {info.resume_text.substring(0, 500)}
                    {info.resume_text.length > 500 && '...'}
                  </pre>
                </details>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'resume');
                  }}
                  disabled={extractingResume}
                  className="hidden"
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className="flex items-center justify-center px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-control cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                  {extractingResume ? 'Extracting text...' : 'Upload Resume (PDF)'}
                </label>
              </div>
            )}
          </div>

          {/* Transcript Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Transcript
            </label>
            {info.transcript_text ? (
              <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-control">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      Transcript text extracted ({info.transcript_text.length} characters)
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteDocument('transcript')}
                    className="text-red-600 hover:text-red-700"
                    title="Clear transcript text"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-blue-600 dark:text-blue-400 hover:underline">
                    View extracted text
                  </summary>
                  <pre className="mt-2 p-2 bg-white dark:bg-slate-800 rounded text-xs overflow-auto max-h-40 text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                    {info.transcript_text.substring(0, 500)}
                    {info.transcript_text.length > 500 && '...'}
                  </pre>
                </details>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'transcript');
                  }}
                  disabled={extractingTranscript}
                  className="hidden"
                  id="transcript-upload"
                />
                <label
                  htmlFor="transcript-upload"
                  className="flex items-center justify-center px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-control cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
                  {extractingTranscript ? 'Extracting text...' : 'Upload Transcript (PDF)'}
                </label>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Personal Statement */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Personal Statement (Optional)
        </h3>
        <textarea
          value={info.personal_statement || ''}
          onChange={(e) => setInfo({ ...info, personal_statement: e.target.value })}
          rows={6}
          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-control bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          placeholder="Write about yourself, your goals, and what makes you unique..."
        />
      </div>

      {/* Projects Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Projects
          </h3>
          <button
            onClick={() => {
              const newProject: Project = {
                id: Date.now().toString(),
                title: '',
                description: '',
                technologies: [],
              };
              setInfo({ ...info, projects: [...info.projects, newProject] });
            }}
            className="flex items-center px-3 py-1 bg-red-600 text-white rounded-control hover:bg-red-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Project
          </button>
        </div>
        <div className="space-y-4">
          {info.projects.map((project, idx) => (
            <ProjectEditor
              key={project.id}
              project={project}
              onChange={(updated) => {
                const updatedProjects = [...info.projects];
                updatedProjects[idx] = updated;
                setInfo({ ...info, projects: updatedProjects });
              }}
              onDelete={() => {
                setInfo({ ...info, projects: info.projects.filter((_, i) => i !== idx) });
              }}
            />
          ))}
          {info.projects.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
              No projects added yet. Click "Add Project" to get started.
            </p>
          )}
        </div>
      </div>

      {/* Job Experiences Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
            <BriefcaseIcon className="h-5 w-5 mr-2" />
            Job Experiences
          </h3>
          <button
            onClick={() => {
              const newJob: JobExperience = {
                id: Date.now().toString(),
                company: '',
                position: '',
                description: '',
                startDate: '',
              };
              setInfo({ ...info, job_experiences: [...info.job_experiences, newJob] });
            }}
            className="flex items-center px-3 py-1 bg-red-600 text-white rounded-control hover:bg-red-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Experience
          </button>
        </div>
        <div className="space-y-4">
          {info.job_experiences.map((job, idx) => (
            <JobEditor
              key={job.id}
              job={job}
              onChange={(updated) => {
                const updatedJobs = [...info.job_experiences];
                updatedJobs[idx] = updated;
                setInfo({ ...info, job_experiences: updatedJobs });
              }}
              onDelete={() => {
                setInfo({ ...info, job_experiences: info.job_experiences.filter((_, i) => i !== idx) });
              }}
            />
          ))}
          {info.job_experiences.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
              No job experiences added yet. Click "Add Experience" to get started.
            </p>
          )}
        </div>
      </div>

      {/* Research Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
            <AcademicCapIcon className="h-5 w-5 mr-2" />
            Research
          </h3>
          <button
            onClick={() => {
              const newResearch: ResearchExperience = {
                id: Date.now().toString(),
                title: '',
                description: '',
              };
              setInfo({ ...info, research: [...info.research, newResearch] });
            }}
            className="flex items-center px-3 py-1 bg-red-600 text-white rounded-control hover:bg-red-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Research
          </button>
        </div>
        <div className="space-y-4">
          {info.research.map((research, idx) => (
            <ResearchEditor
              key={research.id}
              research={research}
              onChange={(updated) => {
                const updatedResearch = [...info.research];
                updatedResearch[idx] = updated;
                setInfo({ ...info, research: updatedResearch });
              }}
              onDelete={() => {
                setInfo({ ...info, research: info.research.filter((_, i) => i !== idx) });
              }}
            />
          ))}
          {info.research.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
              No research experiences added yet. Click "Add Research" to get started.
            </p>
          )}
        </div>
      </div>

      {/* Other Links Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center">
            <LinkIcon className="h-5 w-5 mr-2" />
            Other Links
          </h3>
          <button
            onClick={() => {
              const newLink: OtherLink = {
                id: Date.now().toString(),
                title: '',
                url: '',
              };
              setInfo({ ...info, other_links: [...info.other_links, newLink] });
            }}
            className="flex items-center px-3 py-1 bg-red-600 text-white rounded-control hover:bg-red-700"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Add Link
          </button>
        </div>
        <div className="space-y-4">
          {info.other_links.map((link, idx) => (
            <LinkEditor
              key={link.id}
              link={link}
              onChange={(updated) => {
                const updatedLinks = [...info.other_links];
                updatedLinks[idx] = updated;
                setInfo({ ...info, other_links: updatedLinks });
              }}
              onDelete={() => {
                setInfo({ ...info, other_links: info.other_links.filter((_, i) => i !== idx) });
              }}
            />
          ))}
          {info.other_links.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
              No links added yet. Click "Add Link" to get started.
            </p>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-red-600 text-white rounded-control hover:bg-red-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
}

// Helper components for editing individual items
function ProjectEditor({ project, onChange, onDelete }: { 
  project: Project; 
  onChange: (p: Project) => void; 
  onDelete: () => void;
}) {
  return (
    <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-control">
      <div className="flex justify-between items-start mb-2">
        <input
          type="text"
          value={project.title}
          onChange={(e) => onChange({ ...project, title: e.target.value })}
          placeholder="Project Title"
          className="flex-1 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        />
        <button onClick={onDelete} className="ml-2 text-red-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <textarea
        value={project.description}
        onChange={(e) => onChange({ ...project, description: e.target.value })}
        placeholder="Description"
        rows={3}
        className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white mb-2"
      />
      <input
        type="text"
        value={project.link || ''}
        onChange={(e) => onChange({ ...project, link: e.target.value })}
        placeholder="Project URL (optional)"
        className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
      />
    </div>
  );
}

function JobEditor({ job, onChange, onDelete }: { 
  job: JobExperience; 
  onChange: (j: JobExperience) => void; 
  onDelete: () => void;
}) {
  return (
    <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-control">
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={job.company}
            onChange={(e) => onChange({ ...job, company: e.target.value })}
            placeholder="Company"
            className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          />
          <input
            type="text"
            value={job.position}
            onChange={(e) => onChange({ ...job, position: e.target.value })}
            placeholder="Position"
            className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          />
        </div>
        <button onClick={onDelete} className="ml-2 text-red-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <textarea
        value={job.description}
        onChange={(e) => onChange({ ...job, description: e.target.value })}
        placeholder="Description"
        rows={3}
        className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white mb-2"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          type="date"
          value={job.startDate}
          onChange={(e) => onChange({ ...job, startDate: e.target.value })}
          placeholder="Start Date"
          className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        />
        <input
          type="date"
          value={job.endDate || ''}
          onChange={(e) => onChange({ ...job, endDate: e.target.value || undefined })}
          placeholder="End Date (optional)"
          className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        />
      </div>
    </div>
  );
}

function ResearchEditor({ research, onChange, onDelete }: { 
  research: ResearchExperience; 
  onChange: (r: ResearchExperience) => void; 
  onDelete: () => void;
}) {
  return (
    <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-control">
      <div className="flex justify-between items-start mb-2">
        <input
          type="text"
          value={research.title}
          onChange={(e) => onChange({ ...research, title: e.target.value })}
          placeholder="Research Title"
          className="flex-1 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
        />
        <button onClick={onDelete} className="ml-2 text-red-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <textarea
        value={research.description}
        onChange={(e) => onChange({ ...research, description: e.target.value })}
        placeholder="Description"
        rows={3}
        className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white mb-2"
      />
      <input
        type="text"
        value={research.institution || ''}
        onChange={(e) => onChange({ ...research, institution: e.target.value || undefined })}
        placeholder="Institution (optional)"
        className="w-full px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
      />
    </div>
  );
}

function LinkEditor({ link, onChange, onDelete }: { 
  link: OtherLink; 
  onChange: (l: OtherLink) => void; 
  onDelete: () => void;
}) {
  return (
    <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-control flex items-center gap-2">
      <input
        type="text"
        value={link.title}
        onChange={(e) => onChange({ ...link, title: e.target.value })}
        placeholder="Link Title"
        className="flex-1 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
      />
      <input
        type="url"
        value={link.url}
        onChange={(e) => onChange({ ...link, url: e.target.value })}
        placeholder="URL"
        className="flex-1 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
      />
      <button onClick={onDelete} className="text-red-600">
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
}

